import {
  getAllPlaceMenusByPlaceId,
  getAllPlaceDetailsByPlaceId,
  getPlacePostsByPlaceId,
  getPlaceReviewsSummaryByPlaceId,
  getPlaceTextReviewByPlaceId,
} from "@/api/places";
import { isApiPlace } from "@/utils/placeUtils";
import Iconify from "@/components/ui/Iconify";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import PDW_PlaceMediaPhotoGrid from "../../places-details-window/pdw-place-media-photo-grid";
import PDW_PlaceDescription from "../../places-details-window/pdw-place-description";
import PDW_PlaceTabsSection from "../../places-details-window/pdw-place-tab-section";
import { useTranslations } from "@/lib/static-intl";
import { useAuthContext } from "@/context/auth/useAuthContext";
import PlaceFavSliderModal from "../../rf-place-by-id-window/places-fav-slider-modal";

const PlaceDetailsWindowForSearch = ({
  placeId,
  handleReset,
  userFollowing,
  setUserFollowing,
  handleContentModalOpen,
  handleMapRoutesArrive,
  handleMapRoutesDepart,
  setMapCenter,
  setMapRedPinCenter,
  setPlaceSearchMapPinProperties,
  userLocation,
  selectedLanguage = 'KOR',
}) => {
  // console.log("[PlaceDetailsWindowForSearch] Component rendered with placeId:", placeId);
  const { user } = useAuthContext();
  const apiT = useTranslations("api");
  const t = useTranslations("places.place_details_window");
  const [placeDetails, setPlaceDetails] = useState(null);
  const [placeMenus, setPlaceMenus] = useState([]);
  const [placePosts, setPlacePosts] = useState([]);
  const [placeTextReviews, setPlaceTextReviews] = useState([]);
  const [placesMenuLoader, setPlacesMenuLoader] = useState(true);
  const [placeWindowLoader, setPlaceWindowLoader] = useState(true);

  const thirdChildRef = useRef(null);
  const containerRef = useRef(null);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const [placeMedia, setPlaceMedia] = useState([]);

  const [currentTab, setCurrentTab] = useState("Home");

  const [showPlaceFavModal, setShowPlaceFavModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Math.random());

  const openPlaceFavModal = () => setShowPlaceFavModal(true);
  const closePlaceFavModal = () => setShowPlaceFavModal(false);

  const scrollToThirdChild = () => {
    if (thirdChildRef?.current) {
      thirdChildRef.current.scrollIntoView({
        behavior: "smooth",
        top: 0,
      });
    }
  };

  const changeTabAndScroll = (tab) => {
    if (tab) {
      scrollToThirdChild();
      setCurrentTab(tab);
    }
  };

  const fetchPlaceDetailsById = async () => {
    if (!placeId) {
      // console.log("[PlaceDetailsWindowForSearch] No placeId provided");
      return;
    }
    // console.log("[PlaceDetailsWindowForSearch] Fetching details for placeId:", placeId, "userId:", user?.userId);
    try {
      // console.log("[PlaceDetailsWindowForSearch] Making API call to getAllPlaceDetailsByPlaceId...");
      // Use enriched details endpoint which now also returns PlaceFavorite and review aggregates
      const response = await getAllPlaceDetailsByPlaceId(placeId, user?.userId, selectedLanguage);
      console.log("[PlaceDetailsWindowForSearch] API call completed, response:", response);
      if (response?.id || response?._id || response?.contentId || response?.contentid) {
        // console.log("[PlaceDetailsWindowForSearch] Place details from backend:", response);
        // console.log("[PlaceDetailsWindowForSearch] API place check - source:", response?.source, "placeUniqueNumber:", response?.placeUniqueNumber, "isUserRegistered:", response?.isUserRegistered);
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - response.id:", response?.id);
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - response._id:", response?._id);
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - response.contentid:", response?.contentid);
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - response.contentId:", response?.contentId);
        
        // Test the isApiPlace function here
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - Testing isApiPlace function...");
        // console.log("[DEBUG] PlaceDetailsWindowForSearch - isApiPlace result:", isApiPlace(response));
        setMapRedPinCenter({
          lat: Number(response?.latitude),
          lng: Number(response?.longitude),
        });
        setMapCenter({
          lat: response?.latitude,
          lng: response?.longitude,
        });
        setPlaceDetails(response);
        setPlaceSearchMapPinProperties((prev) => ({
          ...prev,
          properties: {
            ...prev?.properties,
            name: response?.name,
            type: response?.type,
          },
        }));
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setPlaceWindowLoader(false);
    }
  };

  const fetchPlaceMenuDetails = async () => {
    if (!placeId) return;
    try {
      const response = await getAllPlaceMenusByPlaceId(placeId);
      if (Array.isArray(response)) setPlaceMenus(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setPlacesMenuLoader(false);
    }
  };

  const fetchAveragePlaceRating = async () => {
    if (!placeId) return;
    try {
      const response = await getPlaceReviewsSummaryByPlaceId(placeId);
      if (response) {
        setPlaceSearchMapPinProperties((prev) => ({
          ...prev,
          properties: {
            ...prev?.properties,
            ...response,
          },
        }));
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlaceTextReview = async () => {
    if (!placeId) return;
    try {
      const response = await getPlaceTextReviewByPlaceId(placeId);
      if (Array.isArray(response) && response?.length > 0) {
        setPlaceTextReviews(response);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlacePosts = async () => {
    if (!placeId) return;
    try {
      const response = await getPlacePostsByPlaceId(placeId, {
        userId: user?.userId,
      });
      if (Array.isArray(response) && response?.length > 0) {
        setPlacePosts(response);
        if (placePosts?.length !== response?.length) {
          setRefreshKey(Math.random());
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchAllPlaceData = () => {
    setPlaceWindowLoader(true);
    // Reset states to prevent duplication
    setPlaceDetails(null);
    setPlaceMenus([]);
    setPlacePosts([]);
    setPlaceTextReviews([]);
    setPlaceMedia([]);
    
    // Add a small delay to ensure the backend has processed the review
    setTimeout(() => {
      fetchPlaceDetailsById();
      fetchPlaceMenuDetails();
      fetchAveragePlaceRating();
      fetchPlaceTextReview();
      fetchPlacePosts();
    }, 500);
  };

  useEffect(() => {
    if (placeId) {
      fetchAllPlaceData();
    }
    
    // Cleanup function to reset state when placeId changes
    return () => {
      setPlaceDetails(null);
      setPlaceMenus([]);
      setPlacePosts([]);
      setPlaceTextReviews([]);
      setPlaceMedia([]);
      setCurrentTab("Home");
    };
  }, [placeId, user]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current.scrollTop > 10) setIsScrolledDown(true);
      else setIsScrolledDown(false);
    };

    const container = containerRef.current;
    if (container) container.addEventListener("scroll", handleScroll);

    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [placeId]);
  return (
    <div
      ref={containerRef}
      className={`bg-white dark:bg-dark_bg_grey h-full w-full overflow-x-hidden ${
        showPlaceFavModal ? "overflow-y-hidden" : "overflow-y-auto"
      } flex flex-col`}
    >
      {placeWindowLoader ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-5">
          <Iconify
            icon={"eos-icons:bubble-loading"}
            className={"!min-w-10 !h-10"}
          />
          <span className="text-xs text-black/60 dark:text-white/60">
            {t("finding more about this place")}
          </span>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-2">
          <div
            className={`w-full flex items-center justify-between p-3 sticky top-0 left-0 ${
              isScrolledDown
                ? "bg-white dark:bg-dark_bg_grey border-b border-black/10 dark:border-brown"
                : "bg-transparent"
            } z-20 transition ease-in-out duration-150`}
          >
            <div className="w-fit flex items-center gap-1">
              <button type="button" onClick={handleReset}>
                <Iconify
                  icon={"uiw:left"}
                  className={`!min-w-4 !h-4 !text-black`}
                />
              </button>
              {isScrolledDown && (
                <p className="max-w-[330px] truncate">{placeDetails?.name}</p>
              )}
            </div>
            <button type="button" onClick={handleReset}>
              <Iconify
                icon={"material-symbols-light:close"}
                className={`!text-black`}
              />
            </button>
          </div>
          <PDW_PlaceMediaPhotoGrid
            placeId={placeId}
            className={"min-h-[250px] max-h-[250px] gap-0.5 -mt-14"}
            onClick={(tab) => changeTabAndScroll(tab)}
            setPlaceMedia={setPlaceMedia}
            changeTabAndScroll={changeTabAndScroll}
          />
          <PDW_PlaceDescription
            place={placeDetails}
            className={"px-4"}
            isCssForDialog={true}
            handleMapRoutesArrive={handleMapRoutesArrive}
            handleMapRoutesDepart={handleMapRoutesDepart}
            openPlaceFavModal={openPlaceFavModal}
            userLocation={userLocation}
          />
          {!showPlaceFavModal && (
            <PDW_PlaceTabsSection
              key={refreshKey}
              ref={thirdChildRef}
              className={"px-4"}
              scrollToThirdChild={scrollToThirdChild}
              placesMenuLoader={placesMenuLoader}
              place={placeDetails}
              placeMenus={placeMenus}
              placeTextReviews={placeTextReviews}
              placeMedia={placeMedia}
              placePosts={placePosts}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              userFollowing={userFollowing}
              setUserFollowing={setUserFollowing}
              isCssForDialog={true}
              handleContentModalOpen={handleContentModalOpen}
              resetModal={fetchAllPlaceData}
              fetchPlacePosts={fetchPlacePosts}
              fetchPlaceTextReview={fetchPlaceTextReview}
            />
          )}
          <PlaceFavSliderModal
            place={placeDetails}
            placeMedia={placeMedia}
            isOpen={showPlaceFavModal}
            onClose={closePlaceFavModal}
            resetPlaceDetails={fetchAllPlaceData}
          />
        </div>
      )}
    </div>
  );
};

export default PlaceDetailsWindowForSearch;
